import { useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useMatches, useNavigate } from 'react-router-dom';
import { Icon } from '@nazka/nazka.mapframe.components';
import { faChevronDown, faRotateLeft } from '@fortawesome/pro-regular-svg-icons';

import {
  useFlowingWaterSegments,
  useMunicipalityNames,
} from '@services/query.service';
import {
  getMunicipalityRuns,
  getMunicipalitySectorSamples,
  getPotentialGraphPoints,
  orderFlowingWaterSegments,
  segmentBelongsToMunicipality,
} from '@utils/flowing-water-graph.utils';

import {
  setGraphCollapsed,
  setHoveredSegmentId,
  setMunicipalityFilterId,
} from './potential-graph.store';
import PotentialChart, { colors } from './PotentialChart';
import MunicipalityAxis from './MunicipalityAxis/MunicipalityAxis';
import './potential-graph.css';

function PotentialGraph() {
  const matches = useMatches();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const lastMatch = matches[matches.length - 1];
  const stromendWaterId = lastMatch?.params?.stromendWaterId;

  const hoveredSegmentId = useSelector(state => state.flowingWaterGraph.hoveredSegmentId);
  const municipalityFilterId = useSelector(
    state => state.flowingWaterGraph.municipalityFilterId,
  );

  const { data: segments, isLoading } = useFlowingWaterSegments(stromendWaterId);
  const segmentFeatures = useMemo(
    () => (Array.isArray(segments) ? segments : segments?.features || []),
    [segments],
  );

  const orderedSegments = useMemo(
    () => orderFlowingWaterSegments(segmentFeatures),
    [segmentFeatures],
  );
  const filteredSegments = useMemo(
    () => (municipalityFilterId
      ? orderedSegments.filter(
          segment => segmentBelongsToMunicipality(segment, municipalityFilterId),
        )
      : orderedSegments),
    [orderedSegments, municipalityFilterId],
  );

  const points = useMemo(
    () => getPotentialGraphPoints(filteredSegments),
    [filteredSegments],
  );
  const leftRuns = useMemo(() => getMunicipalityRuns(filteredSegments, 'left'), [filteredSegments]);
  const rightRuns = useMemo(() => getMunicipalityRuns(filteredSegments, 'right'), [filteredSegments]);

  const municipalitySamples = useMemo(
    () => getMunicipalitySectorSamples(orderedSegments),
    [orderedSegments],
  );
  const { data: municipalityNames } = useMunicipalityNames(municipalitySamples);

  const activeMunicipalityName = municipalityFilterId
    ? municipalityNames?.[municipalityFilterId] || municipalityFilterId
    : null;

  const handleHover = id => dispatch(setHoveredSegmentId(id));
  const handlePointClick = (id) => {
    if (stromendWaterId == null || id == null) return;

    navigate(`/bronnen/stromend-water/${stromendWaterId}/${id}`);
  };
  const handleMunicipalityClick = (id) => {
    dispatch(setMunicipalityFilterId(id === municipalityFilterId ? null : id));
  };
  const handleReset = () => dispatch(setMunicipalityFilterId(null));
  const handleCollapse = () => dispatch(setGraphCollapsed(true));

  return (
    <div className="graph-panel potential-graph">
      <div className="graph-panel__header potential-graph__header">
        <div className="potential-graph__title">
          <h2 className="graph-panel__header__title">Potentieel grafiek volgens verloop rivier</h2>
        </div>

        <div className="potential-graph__legend" role="group" aria-label="Legende">
          <span className="potential-graph__legend-item">
            <span className="potential-graph__legend-line" style={{ background: colors.deltaT }} />
            DeltaT
          </span>
          <span className="potential-graph__legend-item">
            <span className="potential-graph__legend-line" style={{ background: colors.potential }} />
            Bronpotentieel
          </span>
          <span className="potential-graph__legend-item">
            <span
              className="potential-graph__legend-line potential-graph__legend-line--dashed"
              style={{ color: colors.heatDelivered }}
            />
            Vermogen geleverd
          </span>
        </div>

        <div className="graph-panel__header__actions">
          {activeMunicipalityName && (
            <button
              type="button"
              className="potential-graph__reset"
              onClick={handleReset}
            >
              <Icon icon={faRotateLeft} size="12px" />
              Volledige rivier
            </button>
          )}
          <button
            type="button"
            className="graph-panel__header__actions__action potential-graph__collapse"
            onClick={handleCollapse}
            aria-label="Grafiek sluiten"
          >
            <Icon icon={faChevronDown} size="18px" />
          </button>
        </div>
      </div>

      <div className="graph-panel__content potential-graph__content">
        {points.length > 0
          ? (
              <>
                <PotentialChart
                  points={points}
                  hoveredId={hoveredSegmentId}
                  onHover={handleHover}
                  onClick={handlePointClick}
                />
                <MunicipalityAxis
                  leftRuns={leftRuns}
                  rightRuns={rightRuns}
                  names={municipalityNames}
                  totalCount={filteredSegments.length}
                  activeMunicipalityId={municipalityFilterId}
                  onMunicipalityClick={handleMunicipalityClick}
                />
              </>
            )
          : (
              <div className="potential-graph__empty">
                {isLoading
                  ? 'Potentieelgegevens laden...'
                  : 'Geen potentieelgegevens beschikbaar voor deze rivier.'}
              </div>
            )}
      </div>
    </div>
  );
}

export default PotentialGraph;
